import { getRightMenuConfig as getRightMenuConfigBase } from '@ardoq/settings-bar';
import type { SettingsConfig, ExportConfig } from '@ardoq/view-settings';
import { toggleLegend } from 'presentation/viewPane/actions';
import { ViewIds } from '@ardoq/api-types';
import type { HasViewpointMode } from '@ardoq/graph';
import { dispatchAction } from '@ardoq/rxbeach';
import { isViewEligibleForNewAssets } from 'views/environmentals/restrictedViews';
import { viewpointModeViews$, workspaceModeViews$ } from 'views/modeViews$';
import { toCurrentModeViews } from 'views/environmentals/viewSettings/utils';
import { focusedChromeNow } from 'scope/focusedChromeNow';
import { focusedChromeOperations } from 'scope/focusedChromeOperations';
import { currentUserInterface } from 'modelInterface/currentUser/currentUserInterface';
import { viewLegendCommands } from '@ardoq/view-legend';

type DefaultLegendOnClickArgs = {
  isLegendActive: boolean | undefined;
  viewId: ViewIds;
};
export const defaultLegendOnClick = ({
  isLegendActive,
  viewId,
}: DefaultLegendOnClickArgs) => {
  viewLegendCommands.updateViewLegendSettings({
    viewId,
    isActive: Boolean(isLegendActive),
  });
  dispatchAction(
    toggleLegend({
      viewId: viewId,
      legendActive: !!isLegendActive,
    }),
    viewId
  );
};

type GetRightMenuConfigArgs = Parameters<typeof getRightMenuConfigBase>[0] &
  HasViewpointMode;

const getRightMenuConfig = ({
  isViewpointMode,
  ...args
}: GetRightMenuConfigArgs): SettingsConfig[] => {
  const result = getRightMenuConfigBase(args);
  if (!result.length) {
    return result;
  }
  const exportConfig = result.at(-1) as ExportConfig;
  const userHasWriteAccess =
    currentUserInterface.getCurrentUserAttributes().writeAccess;
  // Called from the render path of every view, none of which has the mode views
  // in its stream; threading them through all of them would only prop up the
  // override below.
  const currentModeViews = toCurrentModeViews(
    isViewpointMode,
    // oxlint-disable-next-line ardoq-lint/no-direct-stream-state-access
    viewpointModeViews$.state,
    // oxlint-disable-next-line ardoq-lint/no-direct-stream-state-access
    workspaceModeViews$.state
  );
  exportConfig.addToPresentation =
    // if a view is in the deprecation phase > 2, "Add to presentation" button should not appear. For more info refer to
    // https://ardoqcom.atlassian.net/wiki/spaces/INSIGHT/pages/2413002789/Discontinued+Views+engineering+perspective.
    // Unfortunately, this is the only function that *all* our view exports have in common, and refactoring is out of
    // scope right now, so I overwrite the "addToPresentation" property here.
    isViewEligibleForNewAssets(currentModeViews.byIds[args.viewId]) &&
    userHasWriteAccess &&
    focusedChromeOperations.allowsAddToPresentation(focusedChromeNow())
      ? exportConfig.addToPresentation
      : undefined;
  return result;
};

export { getRightMenuConfig };
